import { useCallback, useEffect, useState } from 'react';

import { store } from 'redux/store';

import { getBeneficiaryIncentivesMarketContext } from '../api/beneficiaryActions';
import { IncentivesMarketBeneficiaryContext } from '../types/IncentivesMarketBeneficiaryContext';

const emptyContext: IncentivesMarketBeneficiaryContext = {
  cif: '',
  beneficiaryIds: [],
  plans: [],
  totalAvailableUnits: 0,
  reservedUnits: 0,
  maxSellUnits: 0,
};

type UseGetBeneficiaryIncentivesMarketContextProps = {
  societyId?: string;
  beneficiaryId?: string;
  stockPlanId?: string;
  excludeOfferId?: string;
  incentiveOfferId?: string;
  enabled?: boolean;
};

export const useGetBeneficiaryIncentivesMarketContext = ({
  societyId,
  beneficiaryId,
  stockPlanId,
  excludeOfferId,
  incentiveOfferId,
  enabled = true,
}: UseGetBeneficiaryIncentivesMarketContextProps) => {
  const [context, setContext] =
    useState<IncentivesMarketBeneficiaryContext>(emptyContext);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const fetchContext = useCallback(async () => {
    if (!enabled || !societyId || !beneficiaryId) {
      setContext(emptyContext);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setIsError(false);

    try {
      const result: IncentivesMarketBeneficiaryContext = await store.dispatch(
        getBeneficiaryIncentivesMarketContext({
          beneficiaryId,
          societyId,
          stockPlanId,
          excludeOfferId,
          incentiveOfferId,
        })
      );

      setContext(result || emptyContext);
    } catch {
      setIsError(true);
      setContext(emptyContext);
    } finally {
      setIsLoading(false);
    }
  }, [beneficiaryId, enabled, excludeOfferId, incentiveOfferId, societyId, stockPlanId]);

  useEffect(() => {
    fetchContext();
  }, [fetchContext]);

  return {
    data: context,
    isLoading,
    isError,
    refetch: fetchContext,
  };
};
